import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { rbac } from '@dha-nexus/shared';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { useClientPagination } from '../../lib/usePagination.js';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { PageHeader } from '../../app/PageHeader.jsx';
import {
  PageActionStrip,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  AgentCard,
  AiRunQueueCard,
  TelemetryStream,
  AlertCard,
  PageViewerControls,
  toast,
  activityRail,
  KpiHeroBand,
  GaugeCard,
  InlineScoreCell,
  StaggerReveal,
} from '../../components/index.js';
import { ConfirmActionDialog } from '../../components/primitives/ConfirmActionDialog.jsx';
import { GovernedRunChrome } from '../../components/inspectors/GovernedRunChrome.jsx';
import {
  ProvenanceBadge,
  normalizedProvenance,
} from '../../components/ProvenanceBadge.jsx';
import { humanizeEnum } from '../../lib/fieldLabels.js';
import { buildInformationPaperPath } from '../briefings/reportEntryLinks.js';
import './ai.css';
import './ai.redesign.css';

function riskTone(pct) {
  if (pct >= 67) return 'brick';
  if (pct >= 34) return 'amber';
  return 'sage';
}

function confidenceTone(pct) {
  if (pct >= 80) return 'sage';
  if (pct >= 60) return 'amber';
  return 'brick';
}

function confidenceSourceLabel(source) {
  return (
    {
      heuristic: 'Heuristic confidence',
      rules: 'Rules confidence',
      model: 'Model confidence',
      authored: 'Authored confidence',
      unknown: 'Confidence · source unknown',
    }[source] || 'Confidence · source unknown'
  );
}

function executionLine(provenance) {
  if (provenance.executionType === 'rule') {
    return `${provenance.rulesEngine.id} rules engine · v${provenance.rulesEngine.version} · no model invoked`;
  }
  if (provenance.executionType === 'mock') {
    return `${provenance.actualProvider} · simulated assistant · no model invoked`;
  }
  return `${provenance.actualProvider} · ${provenance.modelId} · ${provenance.corpusMode}`;
}

function ProvenanceRunChrome({ result }) {
  if (!result) return null;
  const provenance = normalizedProvenance(result, 'ai');
  return (
    <div className="stack tight" style={{ gap: 8 }}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <ProvenanceBadge value={result} kind="ai" />
        <span className="muted small">{executionLine(provenance)}</span>
        {provenance.confidence.value != null ? (
          <span className="muted small">
            {confidenceSourceLabel(provenance.confidence.source)} ·{' '}
            {Math.round(provenance.confidence.value * 100)}%
          </span>
        ) : null}
      </div>
      <GovernedRunChrome result={{ ...result, confidence: null }} />
    </div>
  );
}

// AI-surface honesty (TT-05 / AC-TRUST-2 / AC-AI-OPS-1): every statistic on
// this page derives from the actual /ai/runs payload or is omitted. No
// string-hash telemetry, no invented success rates, no decorative counters.
const RUN_SUCCESS_RE = /^(completed|succeeded|success)$/i;
const RUN_FAILURE_RE = /^(failed|error|errored)$/i;

function runTimestamp(run) {
  const raw = run.completedAt || run.startedAt || run.createdAt || null;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

function runTelemetryKind(run) {
  const status = String(run.status || '');
  if (RUN_FAILURE_RE.test(status)) return 'error';
  if (/running|processing|analy/i.test(status)) return 'analyzing';
  const review = String(run.reviewState || run.humanReviewState || '').toLowerCase();
  if (review === 'needs_rework') return 'warn';
  if (RUN_SUCCESS_RE.test(status)) return 'success';
  return 'idle';
}

const BLUEPRINTS = [
  {
    id: 'BP-EXEC',
    name: 'Executive briefing',
    desc: 'Quarterly briefing with citations',
    icon: '◆',
  },
  { id: 'BP-RISK', name: 'Risk forecast', desc: 'Predict slip / cost overrun', icon: '⚠' },
  { id: 'BP-COA', name: 'COA analysis', desc: 'Compare options + recommend', icon: '★' },
  { id: 'BP-GATE', name: 'Gate review brief', desc: 'Draft gate decision brief', icon: '⚐' },
];

function formatCitationCount(count = 0) {
  const total = Number(count || 0);
  return `${total} citation${total === 1 ? '' : 's'}`;
}

// Confirm-dialog copy per queue review action (AC-AI-OPS-3).
const QUEUE_REVIEW_META = {
  approve_for_export: {
    title: 'Approve this run for export?',
    body: 'Approving records you as the named human reviewer and unlocks export of products derived from this run.',
    confirmLabel: 'Approve for export',
    tone: 'primary',
  },
  needs_rework: {
    title: 'Send this run back for rework?',
    body: 'The run returns to its requester with your feedback and stays blocked from export.',
    confirmLabel: 'Request rework',
    tone: 'primary',
  },
  reject: {
    title: 'Reject this AI run?',
    body: 'Rejecting records the decision with your name and permanently blocks this output from export.',
    confirmLabel: 'Reject run',
    tone: 'danger',
  },
};

// Explicit scan scope picker (AC-AI-OPS-2): every scan launches against a
// scope the operator explicitly picked — never an implicit hardcoded target.
// When `allowEnterprise` is set, an "Enterprise (all portfolios)" option is
// offered; selecting it clears the scope id so the scan runs across the whole
// enterprise (the API's unscoped path) rather than a single portfolio.
function ScanScopePicker({
  idPrefix,
  scope,
  onChange,
  portfolios,
  projects,
  allowEnterprise = false,
}) {
  const isEnterprise = scope.scopeType === 'enterprise';
  const baseOptions = scope.scopeType === 'project' ? projects : portfolios;
  const options = baseOptions.some((option) => option.id === scope.scopeId)
    ? baseOptions
    : scope.scopeId
      ? [{ id: scope.scopeId, name: scope.scopeId }, ...baseOptions]
      : baseOptions;
  const knownScopeTypes = allowEnterprise
    ? ['portfolio', 'project', 'enterprise']
    : ['portfolio', 'project'];
  return (
    <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <label className="form-field" style={{ minWidth: 140 }}>
        <span className="label">Scan scope type</span>
        <select
          className="input"
          data-testid={`${idPrefix}-scope-type`}
          value={scope.scopeType}
          onChange={(event) => {
            const nextType = event.target.value;
            onChange({
              scopeType: nextType,
              scopeId:
                nextType === 'enterprise'
                  ? ''
                  : (nextType === 'project' ? projects[0]?.id : portfolios[0]?.id) || '',
            });
          }}
        >
          {allowEnterprise ? <option value="enterprise">Enterprise (all portfolios)</option> : null}
          <option value="portfolio">Portfolio</option>
          <option value="project">Project</option>
          {knownScopeTypes.includes(scope.scopeType) ? null : (
            <option value={scope.scopeType}>{scope.scopeType}</option>
          )}
        </select>
      </label>
      {isEnterprise ? null : (
        <label className="form-field" style={{ minWidth: 220 }}>
          <span className="label">Scan scope record</span>
          <select
            className="input"
            data-testid={`${idPrefix}-scope-id`}
            value={scope.scopeId}
            onChange={(event) => onChange({ ...scope, scopeId: event.target.value })}
          >
            {options.length ? null : <option value="">No records available</option>}
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name || option.title || option.id}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}

export function AiOrchestrationHubPage() {
  const { roles = [] } = useCurrentUser();
  const [params] = useSearchParams();
  const focusAgent = params.get('agentId');
  const scopedMode = params.get('mode');
  const scopedScope = params.get('scope');
  const scopedContextType = params.get('scopeType') || 'portfolio';
  const scopedContextIdParam = params.get('scopeId');
  const scopedContextId = scopedContextIdParam || 'PF-CLINICAL-CARE-MOD';
  const scopedReturnTo = params.get('returnTo');
  const scopedReturnLabel = params.get('returnLabel');
  const canSeeProjects = rbac.canSeeRoute('/projects', roles);
  const canSeePortfolios = rbac.canSeeRoute('/portfolios', roles);
  const agents = useApi('/ai/agents', { query: { limit: 30 } });
  const runs = useApi('/ai/runs', { query: { limit: 30 } });
  const projects = useApi('/projects', { query: { limit: 200 }, skip: !canSeeProjects });
  const portfolios = useApi('/portfolios', { query: { limit: 100 }, skip: !canSeePortfolios });
  const review = useMutation();
  const askMut = useMutation();
  const executiveMut = useMutation();
  const redundancyMut = useMutation();
  const predictiveRiskMut = useMutation();
  // S6 (P4 Cap 4): convert a redundancy recommendation into a governed DRAFT decision.
  const consolidationMut = useMutation();
  const [naturalCmd, setNaturalCmd] = useState(
    'Spin up an agent to summarize SPR-DIAG-2026-05A blockers and flag IOC slip risk.',
  );
  const [executiveQuery, setExecutiveQuery] = useState('');
  const [executiveResult, setExecutiveResult] = useState(null);
  const [redundancyResult, setRedundancyResult] = useState(null);
  const [predictiveRiskResult, setPredictiveRiskResult] = useState(null);
  // Explicit scan scope (AC-AI-OPS-2): seeded from deep-link params, then
  // driven by the picker. Every scan posts exactly this scope. Enterprise scope
  // carries no scoping id so the scan runs across every portfolio (the API's
  // unscoped path). Redundancy deep-links from the LOE board default to
  // enterprise; an explicit ?scopeId= (or a non-redundancy surface) preserves
  // the existing portfolio/project deep-link behavior.
  const wantsEnterpriseScope =
    scopedScope === 'enterprise' ||
    scopedContextType === 'enterprise' ||
    (scopedMode === 'redundancy' && !scopedContextIdParam);
  const [scanScope, setScanScope] = useState(
    wantsEnterpriseScope
      ? { scopeType: 'enterprise', scopeId: '' }
      : { scopeType: scopedContextType, scopeId: scopedContextId },
  );
  // Queue review confirm state (AC-AI-OPS-3): confirm + optional note before
  // any decision posts; busyRunId scopes the busy state to the run acted on.
  const [pendingReview, setPendingReview] = useState(null);
  const [busyRunId, setBusyRunId] = useState(null);
  // S6 (P4 Cap 4): the consolidation-review confirm dialog (required rationale) and
  // the map of groupId -> created draft decision record for the success state.
  const [pendingConsolidation, setPendingConsolidation] = useState(null);
  const [consolidationDecisions, setConsolidationDecisions] = useState({});

  const agentList = useMemo(() => agents.data?.data || [], [agents.data?.data]);
  const runList = useMemo(() => runs.data?.data || [], [runs.data?.data]);
  const agentPager = useClientPagination(agentList, {
    initialPageSize: 5,
    resetKey: `agents-${agentList.length}`,
  });
  const runPager = useClientPagination(runList, {
    initialPageSize: 5,
    resetKey: `runs-${runList.length}`,
  });
  const activeAgents = agentList.filter((a) =>
    /running|processing|analy/i.test(a.status || a.lifecycleState || ''),
  );
  const projectNamesById = useMemo(
    () =>
      new Map(
        (projects.data?.data || []).map((project) => [
          String(project.id || ''),
          project.name || project.title || project.id,
        ]),
      ),
    [projects.data?.data],
  );
  const portfolioList = useMemo(() => portfolios.data?.data || [], [portfolios.data?.data]);
  const projectList = useMemo(() => projects.data?.data || [], [projects.data?.data]);
  const redundancyScopeLabel = useMemo(() => {
    if (scanScope.scopeType === 'enterprise') return 'Enterprise (all portfolios)';
    if (!scanScope.scopeId) return 'Active portfolio';
    const lists = [...portfolioList, ...projectList];
    const record = lists.find((row) => row.id === scanScope.scopeId);
    return record?.name || record?.title || scanScope.scopeId;
  }, [scanScope.scopeType, scanScope.scopeId, portfolioList, projectList]);

  // Real run activity for the telemetry stream: only runs the payload actually
  // carries, with their real timestamps. Runs without any timestamp are
  // skipped rather than given an invented one.
  const runTelemetry = useMemo(
    () =>
      runList
        .map((run) => {
          const date = runTimestamp(run);
          if (!date) return null;
          return {
            date,
            ts: `${date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            })} ${date.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })}`,
            kind: runTelemetryKind(run),
            text: run.summary || `${run.agentId || 'Agent'} · ${run.id}`,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.date - b.date)
        .slice(-12)
        .map(({ date: _date, ...entry }) => entry),
    [runList],
  );

  // Per-agent run statistics computed from the runs payload. Agents with no
  // runs in the loaded window get no stats — nothing is invented for them.
  const runStatsByAgent = useMemo(() => {
    const byAgent = new Map();
    for (const run of runList) {
      if (!run.agentId) continue;
      const entry = byAgent.get(run.agentId) || { total: 0, succeeded: 0, citationCount: 0 };
      entry.total += 1;
      if (RUN_SUCCESS_RE.test(String(run.status || ''))) entry.succeeded += 1;
      const citations = Array.isArray(run.citations)
        ? run.citations
        : Array.isArray(run.payload?.citations)
          ? run.payload.citations
          : [];
      entry.citationCount += citations.length;
      byAgent.set(run.agentId, entry);
    }
    return byAgent;
  }, [runList]);

  const runOutcomes = useMemo(() => {
    const total = runList.length;
    const succeeded = runList.filter((run) => RUN_SUCCESS_RE.test(String(run.status || ''))).length;
    return { total, succeeded };
  }, [runList]);

  function agentRunStats(agentId) {
    const stats = runStatsByAgent.get(agentId);
    if (!stats || !stats.total) return [];
    return [
      { label: 'Runs', value: `${stats.total}` },
      { label: 'Success rate', value: `${Math.round((stats.succeeded / stats.total) * 100)}%` },
      { label: 'Citations / run', value: (stats.citationCount / stats.total).toFixed(1) },
    ];
  }

  const pendingReviewCount = useMemo(
    () =>
      runList.filter((run) => {
        const state = run.reviewState || run.humanReviewState || 'pending_human_review';
        return state === 'pending_human_review' || state === 'needs_rework';
      }).length,
    [runList],
  );

  const heroItems = useMemo(() => {
    const items = [
      {
        id: 'agents-online',
        label: 'Agents active',
        value: activeAgents.length,
        status: activeAgents.length > 0 ? 'sage' : 'slate',
        statusLabel: `${agentList.length} registered`,
      },
      {
        id: 'pending-review',
        label: 'Pending human review',
        value: pendingReviewCount,
        status: pendingReviewCount > 0 ? 'amber' : 'sage',
        statusLabel: 'Governance queue',
      },
      {
        id: 'recent-runs',
        label: 'Recent runs',
        value: runList.length,
        status: 'blue',
        statusLabel: 'Last 30',
      },
    ];
    // Real success ratio from run statuses — rendered only when there are
    // runs to measure (AC-AI-OPS-1: no fabricated telemetry).
    if (runOutcomes.total > 0) {
      const pct = Math.round((runOutcomes.succeeded / runOutcomes.total) * 100);
      items.push({
        id: 'run-success',
        label: 'Run success rate',
        value: pct,
        unit: '%',
        status: pct >= 90 ? 'sage' : pct >= 70 ? 'amber' : 'brick',
        statusLabel: `${runOutcomes.succeeded} of ${runOutcomes.total} succeeded`,
      });
    }
    return items;
  }, [activeAgents.length, agentList.length, pendingReviewCount, runList.length, runOutcomes]);

  const riskRanking = useMemo(() => {
    const forecasts = predictiveRiskResult?.forecasts;
    if (!Array.isArray(forecasts) || !forecasts.length) return [];
    return [...forecasts]
      .map((forecast) => ({
        forecast,
        pct: Math.round(Number(forecast.atRiskProbability || 0) * 100),
      }))
      .sort((a, b) => b.pct - a.pct);
  }, [predictiveRiskResult]);

  const topRiskConfidencePct = riskRanking.length
    ? Math.round(Number(riskRanking[0].forecast.confidence || 0) * 100)
    : null;

  async function reviewRun(run, action, note) {
    try {
      setBusyRunId(run.id);
      const res = await review.run(() =>
        api.post(`/ai/runs/${run.id}/review`, { action, note: note || null }),
      );
      toast.success({ title: `Run ${humanizeEnum(action)}`, body: `${run.id} review state updated.` });
      activityRail.push({
        action: `ai_run.${action}`,
        targetType: 'ai_run',
        targetId: run.id,
        actorUserId: 'you',
      });
      runs.setData?.((prev) => {
        if (!prev?.data?.data) return prev;
        return {
          ...prev,
          data: {
            ...prev.data,
            data: prev.data.data.map((currentRun) =>
              currentRun.id === run.id
                ? { ...currentRun, ...(res.data || res), reviewState: action }
                : currentRun,
            ),
          },
        };
      });
    } catch (e) {
      toast.error({ title: 'Review failed', body: e.message });
    } finally {
      setBusyRunId(null);
      setPendingReview(null);
    }
  }

  async function spinUp() {
    try {
      const res = await askMut.run(() => api.post('/ai/ask', { question: naturalCmd }));
      const data = res.data || res;
      toast.success({
        title: 'Agent spun up',
        body: `AIRUN ${data.runId} · ${data.citations?.length || 0} citations attached`,
      });
      activityRail.push({
        action: 'ai.run.spawn',
        targetType: 'ai_run',
        targetId: data.runId,
        actorUserId: 'you',
        summary: naturalCmd.slice(0, 96),
      });
    } catch (e) {
      toast.error({ title: 'Could not spin up', body: e.message });
    }
  }

  async function runExecutiveQuery() {
    try {
      const result = await executiveMut.run(() =>
        api.post('/ai/executive-query', {
          question: executiveQuery,
          scopeType: scanScope.scopeType,
          scopeId: scanScope.scopeId,
        }),
      );
      const payload = result?.data || result;
      setExecutiveResult(payload);
      toast.success({
        title: 'Executive query ready',
        body: `${payload.citations?.length || 0} citations attached for human review.`,
      });
    } catch (e) {
      toast.error({ title: 'Executive query failed', body: e.message });
    }
  }

  async function runRedundancyScan() {
    try {
      const result = await redundancyMut.run(() =>
        api.post('/ai/redundancy-scan', {
          scopeType: scanScope.scopeType,
          scopeId: scanScope.scopeId,
        }),
      );
      const payload = result?.data || result;
      setRedundancyResult(payload);
      toast.success({
        title: 'Redundancy scan complete',
        body: `${payload.groups?.length || 0} overlap groups were queued for review.`,
      });
    } catch (e) {
      toast.error({ title: 'Redundancy scan failed', body: e.message });
    }
  }

  // S6 (P4 Cap 4): open the confirm dialog for a redundancy group. The primary
  // (first) project anchors the project-scoped governed decision.
  function openConsolidationReview(group) {
    setPendingConsolidation({ group });
  }

  async function confirmConsolidationReview(rationale) {
    if (!pendingConsolidation?.group) return;
    const group = pendingConsolidation.group;
    const primaryProjectId = String(group.projectIds?.[0] || '');
    try {
      const result = await consolidationMut.run(() =>
        api.post('/decisions', {
          status: 'draft',
          decisionType: 'consolidationReview',
          scopeType: 'project',
          scopeId: primaryProjectId,
          // The governed contract requires a decision verb even for a draft; this
          // mirrors the group's own recommendation and is resolved by a human later.
          decision: 'review_for_consolidation',
          sourceAiRunId: redundancyResult?.runId || null,
          sourceRecommendationId: group.id,
          rationale,
        }),
      );
      const payload = result?.data || result;
      setConsolidationDecisions((current) => ({
        ...current,
        [group.id]: { ...payload, primaryProjectId },
      }));
      setPendingConsolidation(null);
      toast.success({
        title: 'Consolidation review opened',
        body: `Draft decision ${payload.id || ''} created for a human decision.`,
      });
    } catch (e) {
      toast.error({ title: 'Could not open consolidation review', body: e.message });
    }
  }

  async function runPredictiveRiskScan() {
    try {
      const result = await predictiveRiskMut.run(() =>
        api.post('/ai/predictive-risk', {
          scopeType: scanScope.scopeType,
          scopeId: scanScope.scopeId,
        }),
      );
      const payload = result?.data || result;
      setPredictiveRiskResult(payload);
      toast.success({
        title: 'Predictive risk scan complete',
        body: `${payload.flaggedProjectCount || payload.forecasts?.length || 0} project${
          payload.flaggedProjectCount === 1 ? '' : 's'
        } flagged for review.`,
      });
    } catch (e) {
      toast.error({ title: 'Predictive risk scan failed', body: e.message });
    }
  }

  if (agents.loading) return <LoadingSkeleton rows={6} />;
  if (agents.error) return <ErrorState error={agents.error} />;

  return (
    <>
      {/* PRIME-SHELL-UX-001: single body page identity + relocated About. */}
      <PageHeader />
      <PageActionStrip
        actions={
          <span className={`chip ${activeAgents.length ? 'live' : 'outline'} sm`}>
            {activeAgents.length ? <span className="chip-dot pulse" /> : null}
            {activeAgents.length} of {agentList.length} agents active
          </span>
        }
      />

      <KpiHeroBand
        eyebrow="AI orchestration"
        asOf={runs.loadedAt}
        items={heroItems}
        columns={heroItems.length}
      />

      <section className="bento ai-bento">
        <div className="span-12 card ai-agents-strip">
          <header className="row spread">
            <div>
              <div className="eyebrow">Active operations</div>
              <h2 className="card-title">Agent operations</h2>
            </div>
          </header>
          {agentList.length === 0 ? (
            <EmptyState title="No agents configured" />
          ) : (
            <div className="ai-agent-grid">
              {agentPager.items.map((a) => (
                <AgentCard
                  key={a.id}
                  agent={{
                    ...a,
                    purpose:
                      a.description ||
                      a.purpose ||
                      'Specialist agent in the DHA PRIME orchestrator',
                  }}
                  stats={agentRunStats(a.id)}
                  onRun={(agent) => {
                    setNaturalCmd(`Run ${agent.name} on the current portfolio context.`);
                    toast.show({
                      title: `${agent.name} primed`,
                      body: 'Capture intent and click "Deploy" to start.',
                    });
                  }}
                  onMonitor={(agent) =>
                    toast.show({
                      title: `Monitoring ${agent.name}`,
                      body: 'Telemetry below now scopes to this agent.',
                    })
                  }
                  busy={review.running}
                />
              ))}
            </div>
          )}
          <PageViewerControls
            page={agentPager.page}
            label="agents"
            pageSize={agentPager.pageSize}
            onOffsetChange={agentPager.setOffset}
            onPageSizeChange={agentPager.setPageSize}
          />
        </div>

        <div className="span-8 card ai-runs">
          <header className="row spread">
            <div>
              <div className="eyebrow">Human review queue</div>
              <h2 className="card-title">Pending decisions</h2>
            </div>
            <span className="chip outline sm">{runList.length} recent runs</span>
          </header>
          {runList.length === 0 ? (
            <EmptyState title="No runs yet" />
          ) : (
            <div className="ai-run-stack">
              {runPager.items.map((r) => {
                const provenance = normalizedProvenance(r, 'ai');
                return (
                  <div key={r.id} className="stack tight" style={{ gap: 6 }}>
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                      <ProvenanceBadge value={r} kind="ai" />
                      <span className="muted small">{executionLine(provenance)}</span>
                      {provenance.confidence.value != null ? (
                        <span
                          className="muted small"
                          data-testid={`ai-run-confidence-${r.id}`}
                        >
                          {confidenceSourceLabel(provenance.confidence.source)} ·{' '}
                          {Math.round(provenance.confidence.value * 100)}%
                        </span>
                      ) : null}
                    </div>
                    <AiRunQueueCard
                      run={{
                        ...r,
                        title: r.title || r.summary?.slice(0, 80) || r.id,
                        citations: r.citations || (r.payload?.citations ? r.payload.citations : []),
                        confidence: undefined,
                      }}
                      onApprove={(run) =>
                        setPendingReview({ run, action: 'approve_for_export' })
                      }
                      onRework={(run) => setPendingReview({ run, action: 'needs_rework' })}
                      onReject={(run) => setPendingReview({ run, action: 'reject' })}
                      onOpen={(run) => (window.location.href = `/ai/runs/${run.id}`)}
                      busy={review.running && busyRunId === r.id}
                    />
                  </div>
                );
              })}
            </div>
          )}
          <PageViewerControls
            page={runPager.page}
            label="AI runs"
            pageSize={runPager.pageSize}
            onOffsetChange={runPager.setOffset}
            onPageSizeChange={runPager.setPageSize}
          />
        </div>

        <div className="span-4 stack">
          {runTelemetry.length ? <TelemetryStream entries={runTelemetry} maxHeight={260} /> : null}
          <article className="card ai-naturalcmd">
            <header>
              <div className="eyebrow">Natural command</div>
              <h3 className="card-title">Spin up an agent</h3>
            </header>
            <textarea
              className="ai-cmd-input"
              rows={3}
              value={naturalCmd}
              onChange={(e) => setNaturalCmd(e.target.value)}
              placeholder="Tell the orchestrator what you need…"
            />
            <button
              className="btn primary tactile-press"
              onClick={spinUp}
              disabled={askMut.running}
            >
              {askMut.running ? 'Deploying…' : 'Deploy agent'}
            </button>
            <AlertCard
              severity="blue"
              title="Restricted actions"
              body="AI cannot autonomously approve or deny intake, assign resources, approve budget or forecast changes, publish official products, close lifecycle gates, or change access or RBAC. Humans approve every state change."
            />
          </article>
          <article className="card ai-naturalcmd">
            <header>
              <div className="eyebrow">Governed outputs</div>
              <h3 className="card-title">Executive query</h3>
            </header>
            <label className="eyebrow" htmlFor="executive-query">
              Executive query
            </label>
            <div className="stack tight" style={{ gap: 4, marginBottom: 8 }}>
              <span className="eyebrow">Query scope</span>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <span className="chip blue sm">{humanizeEnum(scanScope.scopeType)}</span>
                <span className="chip outline sm">{redundancyScopeLabel}</span>
                {scopedReturnTo ? (
                  <Link className="btn ghost sm" to={scopedReturnTo}>
                    Back to {scopedReturnLabel || redundancyScopeLabel}
                  </Link>
                ) : null}
              </div>
            </div>
            <textarea
              id="executive-query"
              className="ai-cmd-input"
              rows={3}
              value={executiveQuery}
              onChange={(e) => setExecutiveQuery(e.target.value)}
              placeholder="Ask for an executive-ready mission or delivery synthesis."
            />
            <button
              className="btn primary tactile-press"
              onClick={runExecutiveQuery}
              disabled={executiveMut.running || !executiveQuery.trim()}
            >
              {executiveMut.running ? 'Running…' : 'Run executive query'}
            </button>
            {executiveResult ? (
              <div className="stack" style={{ gap: 8 }}>
                <p>{executiveResult.answer}</p>
                {executiveResult.masking?.applied ? (
                  <div className="stack tight" style={{ gap: 4 }}>
                    <span className="chip outline sm">Masked by role</span>
                    <p className="muted small">{executiveResult.masking.summary}</p>
                  </div>
                ) : null}
                <ProvenanceRunChrome result={executiveResult} />
              </div>
            ) : null}
          </article>
        </div>

        <div className="span-12 card ai-blueprints">
          <header className="row spread">
            <div>
              <div className="eyebrow">Pre-configured blueprints</div>
              <h2 className="card-title">Start from a proven recipe</h2>
            </div>
            {focusAgent ? <span className="chip blue sm">Filtered: {focusAgent}</span> : null}
          </header>
          <div className="ai-blueprint-grid">
            {BLUEPRINTS.map((bp) => (
              <article key={bp.id} className="ai-blueprint">
                <span className="ai-blueprint-icon" aria-hidden>
                  {bp.icon}
                </span>
                <div>
                  <strong>{bp.name}</strong>
                  <p className="muted small">{bp.desc}</p>
                </div>
                <button
                  className="btn link"
                  onClick={() => {
                    setNaturalCmd(`Run ${bp.name} for the active portfolio context.`);
                    toast.show({ title: 'Blueprint loaded', body: bp.name });
                  }}
                >
                  Load →
                </button>
              </article>
            ))}
          </div>
        </div>

        <div className="span-12 card ai-blueprints">
          <header className="row spread">
            <div>
              <div className="eyebrow">Predictive project risk</div>
              <h2 className="card-title">At-risk portfolio forecast</h2>
            </div>
            <button
              className="btn secondary"
              onClick={runPredictiveRiskScan}
              disabled={predictiveRiskMut.running || !scanScope.scopeId}
            >
              {predictiveRiskMut.running ? 'Scanning…' : 'Run predictive risk scan'}
            </button>
          </header>
          <div style={{ marginBottom: 12 }}>
            <ScanScopePicker
              idPrefix="predictive-scan"
              scope={scanScope}
              onChange={setScanScope}
              portfolios={portfolioList}
              projects={projectList}
            />
          </div>
          {!predictiveRiskResult?.forecasts?.length ? (
            <p className="muted small">
              Generate a governed scan to surface emerging schedule, cost, staffing, and evidence
              risks before they turn red on the Director dashboard.
            </p>
          ) : (
            <div className="stack" style={{ gap: 12 }}>
              <ProvenanceRunChrome result={predictiveRiskResult} />
              <div className="bento" style={{ gap: 16 }}>
                <div className="span-8 ai-orchestration-confidence-cell">
                  <span className="eyebrow">At-risk ranking</span>
                  <StaggerReveal className="ai-risk-rank">
                    {riskRanking.map(({ forecast, pct }, idx) => (
                      <div
                        key={forecast.projectId || forecast.projectName || idx}
                        className="ai-risk-rank-row"
                        data-testid={`ai-risk-rank-${forecast.projectId || idx}`}
                      >
                        <span className="ai-risk-rank-idx">{idx + 1}</span>
                        <span
                          className="ai-risk-rank-name"
                          title={forecast.projectName || forecast.projectId}
                        >
                          {forecast.projectName || forecast.projectId}
                        </span>
                        <InlineScoreCell
                          value={pct}
                          max={100}
                          suffix="%"
                          tone={riskTone(pct)}
                          ariaLabel={`${forecast.projectName || forecast.projectId} ${pct}% at risk`}
                        />
                      </div>
                    ))}
                  </StaggerReveal>
                </div>
                {topRiskConfidencePct != null ? (
                  <div className="span-4 ai-orchestration-confidence-cell">
                    <GaugeCard
                      id="predictive-top-confidence"
                      value={topRiskConfidencePct}
                      min={0}
                      max={100}
                      unit="%"
                      label="Top forecast confidence"
                      sublabel={riskRanking[0]?.forecast.projectName || 'Highest at-risk project'}
                      tone={confidenceTone(topRiskConfidencePct)}
                    />
                  </div>
                ) : null}
              </div>
              <div className="ai-blueprint-grid">
                {predictiveRiskResult.forecasts.map((forecast) => (
                  <article key={forecast.projectId} className="ai-blueprint">
                    <span className="ai-blueprint-icon" aria-hidden>
                      ⚠
                    </span>
                    <div className="stack" style={{ gap: 8 }}>
                      <strong>{forecast.projectName}</strong>
                      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                        <span className="chip blue sm">
                          {Math.round((forecast.atRiskProbability || 0) * 100)}% at risk
                        </span>
                        <span className="chip outline sm">
                          {Math.round((forecast.confidence || 0) * 100)}% confidence
                        </span>
                        {forecast.impactedLoes?.map((loe) => (
                          <span key={loe} className="chip outline sm">
                            {loe}
                          </span>
                        ))}
                      </div>
                      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                        {forecast.reasonCodes?.map((reasonCode) => (
                          <span key={reasonCode} className="chip outline sm" title={reasonCode}>
                            {humanizeEnum(reasonCode)}
                          </span>
                        ))}
                      </div>
                      {forecast.leadingIndicators?.length ? (
                        <div className="stack tight" style={{ gap: 2 }}>
                          <span className="eyebrow">Leading indicator</span>
                          <p className="muted small">{forecast.leadingIndicators[0]}</p>
                        </div>
                      ) : null}
                      {forecast.recommendedMitigationActions?.length ? (
                        <div className="stack tight" style={{ gap: 2 }}>
                          <span className="eyebrow">Mitigation suggestion</span>
                          <p className="muted small">{forecast.recommendedMitigationActions[0]}</p>
                        </div>
                      ) : null}
                      <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                        <span className="muted small">
                          {forecast.forecastedScheduleImpactDays} day schedule slip
                        </span>
                        <span className="muted small">
                          {forecast.forecastedCostImpactUsdM}M projected cost pressure
                        </span>
                        <span className="muted small">
                          {formatCitationCount(forecast.citations?.length || 0)}
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="span-12 card ai-blueprints">
          <header className="row spread">
            <div>
              <div className="eyebrow">
                {scopedMode === 'redundancy' ? 'Scoped overlap review' : 'Portfolio overlap'}
              </div>
              <h2 className="card-title">Redundancy scan</h2>
            </div>
            <button
              className="btn secondary"
              onClick={runRedundancyScan}
              disabled={
                redundancyMut.running ||
                (scanScope.scopeType !== 'enterprise' && !scanScope.scopeId)
              }
            >
              {redundancyMut.running ? 'Scanning…' : 'Run redundancy scan'}
            </button>
          </header>
          <div style={{ marginBottom: 12 }}>
            <ScanScopePicker
              idPrefix="redundancy-scan"
              scope={scanScope}
              onChange={setScanScope}
              portfolios={portfolioList}
              projects={projectList}
              allowEnterprise
            />
          </div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <span className="chip blue sm">{humanizeEnum(scanScope.scopeType)}</span>
            <span className="chip outline sm">{redundancyScopeLabel}</span>
            {scopedReturnTo ? (
              <Link className="btn ghost sm" to={scopedReturnTo}>
                Back to {scopedReturnLabel || redundancyScopeLabel}
              </Link>
            ) : null}
          </div>
          {!redundancyResult ? (
            <p className="muted small">
              Scan the active scope to find overlapping mission threads, sustainment duplication,
              and consolidation candidates.
            </p>
          ) : !redundancyResult.groups?.length ? (
            // BL-22 (tester feedback): a clean result must be actionable. A
            // single-portfolio scope can legitimately hold zero overlaps —
            // especially the portfolio NAMED "Enterprise Sustainment", which
            // reads like the all-portfolios option — so say what was scanned
            // and how to widen it instead of leaving the pre-scan copy up.
            <p className="muted small" data-testid="redundancy-empty-state">
              {scanScope.scopeType === 'enterprise' ? (
                <>No overlap groups were found across any portfolio — nothing is queued for review.</>
              ) : (
                <>
                  No overlap groups were found in this scope. Overlapping efforts often live across
                  portfolios: switch the scope to <strong>Enterprise (all portfolios)</strong> — the
                  first entry in the scope picker — and run the scan again.
                </>
              )}
            </p>
          ) : (
            <div className="stack" style={{ gap: 12 }}>
              <ProvenanceRunChrome result={redundancyResult} />
              <div className="ai-blueprint-grid">
                {redundancyResult.groups.map((group) => (
                  <article key={group.id} className="ai-blueprint">
                    <span className="ai-blueprint-icon" aria-hidden>
                      ⟲
                    </span>
                    <div className="stack" style={{ gap: 8 }}>
                      <strong title={group.recommendation}>
                        {humanizeEnum(group.recommendation)}
                      </strong>
                      {group.rationale ? (
                        <div className="stack tight" style={{ gap: 2 }}>
                          <span className="eyebrow">Rationale</span>
                          <p className="muted small">{group.rationale}</p>
                        </div>
                      ) : null}
                      {group.consolidationRisk ? (
                        <div className="stack tight" style={{ gap: 4 }}>
                          <span className="eyebrow">Consolidation risk</span>
                          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                            <span className="chip outline sm">
                              {humanizeEnum(group.consolidationRisk.level)}
                            </span>
                            <span className="muted small">{group.consolidationRisk.summary}</span>
                          </div>
                        </div>
                      ) : null}
                      <p className="muted small">
                        {group.projectIds
                          .map((projectId) => projectNamesById.get(projectId) || projectId)
                          .join(' + ')}
                      </p>
                      <p className="muted small">{group.evidence?.length || 0} evidence links</p>
                      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                        {group.projectIds.map((projectId) => (
                          <Link
                            key={`${group.id}-${projectId}-workspace`}
                            className="btn ghost sm"
                            to={`/projects/${projectId}`}
                          >
                            Open {projectId}
                          </Link>
                        ))}
                      </div>
                      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                        {group.projectIds.map((projectId) => (
                          <Link
                            key={`${group.id}-${projectId}-briefing`}
                            className="btn ghost sm"
                            to={buildInformationPaperPath({
                              scopeType: 'project',
                              scopeId: projectId,
                              sourceAiRunId: redundancyResult.runId,
                            })}
                          >
                            Draft Information Paper for {projectId}
                          </Link>
                        ))}
                      </div>
                      {/* S6 (P4 Cap 4): governed handoff — open a DRAFT consolidation
                          decision for a human to decide. Never auto-decides. */}
                      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="btn secondary sm"
                          onClick={() => openConsolidationReview(group)}
                          disabled={Boolean(consolidationDecisions[group.id])}
                        >
                          Open consolidation review (draft decision)
                        </button>
                      </div>
                      {consolidationDecisions[group.id] ? (
                        <div
                          className="chip green sm"
                          data-testid={`consolidation-review-success-${group.id}`}
                        >
                          Draft decision {consolidationDecisions[group.id].id} opened —{' '}
                          <Link
                            to={`/projects/${consolidationDecisions[group.id].primaryProjectId}`}
                          >
                            open scoped project
                          </Link>
                        </div>
                      ) : null}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      <ConfirmActionDialog
        open={Boolean(pendingReview)}
        title={
          (pendingReview && QUEUE_REVIEW_META[pendingReview.action]?.title) ||
          'Confirm review decision'
        }
        body={
          pendingReview
            ? `${QUEUE_REVIEW_META[pendingReview.action]?.body || ''} (Run ${pendingReview.run.id})`
            : null
        }
        confirmLabel={
          (pendingReview && QUEUE_REVIEW_META[pendingReview.action]?.confirmLabel) || 'Confirm'
        }
        tone={(pendingReview && QUEUE_REVIEW_META[pendingReview.action]?.tone) || 'primary'}
        showNote
        noteLabel="Review note"
        noteHint="Optional — recorded with the decision and shown on the run dossier."
        busy={review.running}
        onConfirm={(note) =>
          pendingReview && reviewRun(pendingReview.run, pendingReview.action, note)
        }
        onCancel={() => setPendingReview(null)}
      />
      <ConfirmActionDialog
        open={Boolean(pendingConsolidation)}
        title="Open consolidation review"
        body={
          pendingConsolidation
            ? `Open a governed draft decision to review consolidating ${pendingConsolidation.group.projectIds
                .map((projectId) => projectNamesById.get(projectId) || projectId)
                .join(' + ')}. A human decides the outcome — this only opens the review.`
            : null
        }
        confirmLabel="Open consolidation review"
        tone="primary"
        requireNote
        noteLabel="Rationale"
        noteHint="A typed rationale is required — it is recorded on the draft decision."
        notePlaceholder="Document why these efforts should be reviewed for consolidation."
        busy={consolidationMut.running}
        onConfirm={(note) => confirmConsolidationReview(note)}
        onCancel={() => setPendingConsolidation(null)}
      />
    </>
  );
}
